import { NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  AddressElements,
  LocationData,
  LocationDataGeocoded,
  HTMLElementEvent,
} from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { SnackbarService } from '@sc/feature-snackbar';
import { ButtonComponent, InputTextComponent } from '@sc/ui-components';
import { Subject } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { PlanningSandboxService } from '../../../services';

@Component({
  selector: 'sc-planning-form-address-field',
  styleUrls: ['./planning-form-address-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, ButtonComponent, InputTextComponent, TranslateModule],
  template: `
    <ng-container>
      <span class="address-bloc">
        <div class="buttons-container">
          <sc-button
            *ngIf="unkownAddressSelected"
            buttotType="secondary"
            leftIcon="close"
            class="close-details-btn"
            (click)="unkownAddressSelected = false"
            [disabled]="disabled"
          >
            {{
              'PlanningFormAppointmentMobility.unknownAdressButton' | translate
            }}
          </sc-button>
          <sc-button
            *ngIf="nonCompleteAddrSelected && !unkownAddressSelected"
            buttotType="secondary"
            class="open-filled-addr-form-btn"
            (click)="openManualAddressFormFilled()"
            [disabled]="disabled"
          >
            {{
              'PlanningFormAppointmentMobility.nonCompleteAdressButton'
                | translate
            }}
          </sc-button>
        </div>

        <div class="address-bloc" *ngIf="unkownAddressSelected">
          <div class="form-row">
            <div class="form-data">
              <sc-input-text
                type="text"
                [label]="
                  'PlanningFormAppointmentMobility.streetNum' | translate
                "
                [value]="modifiedLocation?.addressElements?.streetNumber"
                (input)="onChangeAddressElement($event, 'streetNumber')"
                [disabled]="disabled"
              ></sc-input-text>
            </div>
            <div class="form-data">
              <sc-input-text
                type="text"
                [label]="
                  'PlanningFormAppointmentMobility.streetType' | translate
                "
                [value]="modifiedLocation?.addressElements?.streetSuffix"
                (input)="onChangeAddressElement($event, 'streetSuffix')"
                [disabled]="disabled"
              ></sc-input-text>
            </div>
          </div>
          <div class="form-row">
            <div class="form-data">
              <sc-input-text
                type="text"
                [label]="
                  'PlanningFormAppointmentMobility.streetName' | translate
                "
                [value]="modifiedLocation?.addressElements?.streetName"
                (input)="onChangeAddressElement($event, 'streetName')"
                [disabled]="disabled"
              ></sc-input-text>
            </div>
          </div>
          <div class="form-row">
            <div class="form-data">
              <sc-input-text
                type="text"
                [asterisk]="true"
                [label]="
                  'PlanningFormAppointmentMobility.postalCode' | translate
                "
                [value]="modifiedLocation?.addressElements?.postalCode"
                (input)="onChangeAddressElement($event, 'postalCode')"
                [disabled]="disabled"
              ></sc-input-text>
            </div>
            <div class="form-data">
              <sc-input-text
                type="text"
                [asterisk]="true"
                [label]="
                  'PlanningFormAppointmentMobility.cityLabel' | translate
                "
                [value]="modifiedLocation?.addressElements?.city"
                (input)="onChangeAddressElement($event, 'city')"
                [disabled]="disabled"
              ></sc-input-text>
            </div>
          </div>
        </div>

        <div class="address-bloc">
          <div class="form-row" *ngIf="!unkownAddressSelected">
            <div class="form-data">
              <sc-input-text
                type="text"
                [label]="
                  'PlanningFormAppointmentMobility.addressLabel' | translate
                "
                suggestionsLabelName="address"
                [value]="formatSelectedAddress()"
                [hint]="getAutocompleteHintText()"
                [suggestions]="suggestedLocations"
                [forceDisplayError]="isAddressInputError"
                (input)="onAddressChangeValue($event)"
                (updateSuggestions)="onUpdateAddressSuggestions($event)"
                (selectSuggestion)="onSelectAddress($event)"
                [disabled]="disabled"
              ></sc-input-text>
            </div>
            <div class="form-data city-display-container">
              <sc-input-text
                type="text"
                [label]="
                  'PlanningFormAppointmentMobility.cityLabel' | translate
                "
                [value]="getCityLabel()"
                [forceDisplayError]="false"
                [disabled]="true"
              ></sc-input-text>
            </div>
          </div>
        </div>
      </span>
    </ng-container>
  `,
})
export class PlanningFormAddressFieldComponent implements OnDestroy {
  @Input() disabled: boolean;
  @Input() set searchLocationData(newLocation: LocationDataGeocoded) {
    this.selectedSearchLocationGeoCodedData = newLocation;
    this.selectedAddressStr =
      this.selectedSearchLocationGeoCodedData?.address ?? '';
  }

  @Output() changeAddress = new EventEmitter<LocationDataGeocoded>();

  unkownAddressSelected = false;

  nonCompleteAddrSelected = false;

  selectedSearchLocationGeoCodedData: LocationDataGeocoded = null;

  selectedAddressStr: string;

  modifiedLocation: LocationDataGeocoded = null;

  suggestedLocations: LocationData[] = [];

  isAddressInputError: boolean;
  private readonly addressSuggestionsSubject$ = new Subject<string>();

  constructor(
    private readonly planningSandbox: PlanningSandboxService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly translator: TranslatorService,
    private readonly snackbarService: SnackbarService
  ) {
    this.addressSuggestionsSubject$
      .pipe(
        debounceTime(500),
        filter((value) => value.length >= 3)
      )
      .subscribe((value: string) => {
        this.planningSandbox.getPlacesFromAddress(value).subscribe({
          next: (places) => {
            this.suggestedLocations = places;
            this.suggestedLocations.push({
              address: this.translator.getInstantTranslation(
                'PlanningFormAppointmentMobility.unknownAddressValue'
              ),
              isCompleteAddress: false,
              isKnownAddress: false,
              locationProvider: 'NONE',
              placeId: null,
            });
            this.changeDetectorRef.markForCheck();
          },
          error: () => {
            this.suggestedLocations = [];
            this.suggestedLocations.push({
              address: this.translator.getInstantTranslation(
                'PlanningFormAppointmentMobility.unknownAddressValue'
              ),
              isCompleteAddress: false,
              isKnownAddress: false,
              locationProvider: 'NONE',
              placeId: null,
            });
            this.changeDetectorRef.markForCheck();
          },
        });
      });
  }

  openManualAddressForm(autoFillLocation?: LocationDataGeocoded) {
    this.modifiedLocation = {
      placeId: null,
      locationProvider: 'NONE',
      isKnownAddress: false,
      isCompleteAddress: false,
      addressElements: {
        ...(autoFillLocation?.addressElements || {}),
        country: 'fr',
      },
      geoCoordinates: null,
      address: autoFillLocation?.address || '',
    };

    this.unkownAddressSelected = true;
  }

  openManualAddressFormFilled() {
    this.openManualAddressForm(this.selectedSearchLocationGeoCodedData);
  }

  onChangeAddressElement($event: Event, elementName: keyof AddressElements) {
    if (this.modifiedLocation) {
      this.modifiedLocation.addressElements[elementName] = (
        $event as HTMLElementEvent<HTMLInputElement>
      ).target.value;
      this.updateFormModifiedAddress();
    }
  }

  updateFormModifiedAddress() {
    this.modifiedLocation = {
      ...this.modifiedLocation,
      address: this.getAddress(),
    };

    this.changeAddress.emit(this.modifiedLocation);
  }

  getAddress() {
    const suffixAndName = [
      this.modifiedLocation?.addressElements?.streetSuffix,
      this.modifiedLocation?.addressElements?.streetName,
    ]
      .filter((item) => !!item)
      .join(' ');

    const postalCodeAndCity = [
      this.modifiedLocation?.addressElements?.postalCode,
      this.modifiedLocation?.addressElements?.city,
    ]
      .filter((item) => !!item)
      .join(' ');

    let resultAddr = [
      this.modifiedLocation?.addressElements?.streetNumber,
      suffixAndName,
      postalCodeAndCity,
    ]
      .filter((item) => !!item)
      .join(', ');
    if (
      resultAddr.includes('[object Object]') ||
      resultAddr.includes('undefined') ||
      resultAddr.includes('null')
    ) {
      resultAddr = this.translator.getInstantTranslation(
        'PlanningFormAppointmentMobility.unknownAddress'
      );
    }

    return resultAddr;
  }

  formatSelectedAddress() {
    return (
      [
        this.selectedSearchLocationGeoCodedData?.addressElements?.streetNumber,
        this.selectedSearchLocationGeoCodedData?.addressElements?.streetName,
      ]
        .filter((streetInfo) => !!streetInfo)
        .join(' ') || this.selectedAddressStr
    );
  }

  getAutocompleteHintText(): string {
    // TODO add required check
    return this.selectedSearchLocationGeoCodedData?.isCompleteAddress !==
      true && this.isAddressInputError
      ? this.translator.getInstantTranslation(
          'PlanningFormAppointmentMobility.addressHint'
        )
      : '';
  }

  onAddressChangeValue($event) {
    const value = $event?.target?.value;
    this.isAddressInputError = !value || value !== this.selectedAddressStr;
  }

  onUpdateAddressSuggestions(value: string) {
    if (value !== this.selectedAddressStr) {
      this.addressSuggestionsSubject$.next(value);
    }
  }

  ngOnDestroy(): void {
    this.addressSuggestionsSubject$.complete();
  }

  onSelectAddress(locationData: LocationData) {
    if (locationData.placeId) {
      this.modifiedLocation = null;
      this.unkownAddressSelected = false;
      this.planningSandbox
        .getGeoCodeFromPlaceId(locationData.placeId)
        .subscribe({
          next: (location) => {
            this.selectedAddressStr = location.address;
            this.selectedSearchLocationGeoCodedData = location;
            this.changeAddress.emit(location);

            this.nonCompleteAddrSelected = !location.isCompleteAddress;

            this.isAddressInputError =
              !this.planningSandbox.isMinimumAddrElementsGiven(location);
            this.changeDetectorRef.markForCheck();
          },
          error: (error: HttpErrorResponse) => {
            this.snackbarService.showError(
              this.translator.getInstantTranslation('Snackbar.errorTitle'),
              this.translator.getInstantTranslation(
                error?.error?.message ||
                  'PlanningFormAppointmentMobility.getAddressError'
              )
            );

            console.error(error);
          },
        });
    } else {
      const firstProposedLocation = this.suggestedLocations?.[0];
      this.planningSandbox
        .getGeoCodeFromPlaceId(firstProposedLocation.placeId)
        .subscribe({
          next: (location) => {
            this.changeAddress.emit(location);
            this.openManualAddressForm(location);
          },
          error: (error: HttpErrorResponse) => {
            this.changeAddress.emit(null);
            this.openManualAddressForm();
            this.snackbarService.showError(
              this.translator.getInstantTranslation('Snackbar.errorTitle'),
              this.translator.getInstantTranslation(
                error?.error?.message ||
                  'PlanningFormAppointmentMobility.getAddressError'
              )
            );

            console.error(error);
          },
        });
    }
  }

  getCityLabel() {
    return this.selectedSearchLocationGeoCodedData?.addressElements?.postalCode
      ? this.selectedSearchLocationGeoCodedData?.addressElements?.postalCode +
          ' ' +
          (this.selectedSearchLocationGeoCodedData?.addressElements?.city ?? '')
      : this.selectedSearchLocationGeoCodedData?.addressElements?.city
      ? this.selectedSearchLocationGeoCodedData?.addressElements?.city
      : this.selectedSearchLocationGeoCodedData?.address;
  }
}
